import { useMemo, type ReactNode } from "react";

import { Tooltip } from "@/components/Tooltip";
import { formatDuration, formatMetric } from "@/lib/format";
import type { SizeVariant, WidgetComponentProps } from "../types";
import { useSummaryQuery } from "../useSummaryQuery";

type Accent = "orange" | "cyan" | "yellow" | "green" | "red";

const ACCENT_HEX: Record<Accent, string> = {
  orange: "#FF6B1A",
  cyan: "#38BDF8",
  yellow: "#FBBF24",
  green: "#4ADE80",
  red: "#EF4444",
};

function dailyBuckets(flightDates: string[], days: number): number[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayMs = 24 * 3600 * 1000;
  const counts = new Array<number>(days).fill(0);
  for (const dateStr of flightDates) {
    const d = new Date(dateStr);
    d.setHours(0, 0, 0, 0);
    const delta = Math.floor((today.getTime() - d.getTime()) / dayMs);
    if (delta >= 0 && delta < days) {
      counts[days - 1 - delta] += 1;
    }
  }
  return counts;
}

function Sparkline({
  values,
  accent,
  height = 36,
}: {
  values: number[];
  accent: Accent;
  height?: number;
}) {
  if (!values.length) return null;
  const max = Math.max(1, ...values);
  const total = values.reduce((a, b) => a + b, 0);
  const width = 100;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const points = values
    .map((v, i) => `${i * step},${height - (v / max) * (height - 4) - 2}`)
    .join(" ");
  const lastX = (values.length - 1) * step;
  const lastY = height - (values[values.length - 1] / max) * (height - 4) - 2;
  const today = values[values.length - 1];
  const yesterday = values[values.length - 2] ?? 0;
  const delta = today - yesterday;

  return (
    <Tooltip
      content={
        <>
          <strong>Recent {values.length}-day activity</strong>
          <dl>
            <dt>Today</dt>
            <dd>{today}</dd>
            <dt>Yesterday</dt>
            <dd>{yesterday}</dd>
            <dt>Peak</dt>
            <dd>{max}</dd>
            <dt>Total</dt>
            <dd>{total}</dd>
            <dt>Change</dt>
            <dd>
              {delta > 0 ? "+" : ""}
              {delta}
            </dd>
          </dl>
        </>
      }
    >
      <svg
        className="kpi-widget__sparkline"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        width="100%"
        height={height}
      >
        <polyline
          fill="none"
          stroke={ACCENT_HEX[accent]}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
        <polyline
          fill={ACCENT_HEX[accent]}
          fillOpacity="0.12"
          stroke="none"
          points={`0,${height} ${points} ${lastX},${height}`}
        />
        <circle cx={lastX} cy={lastY} r="2" fill={ACCENT_HEX[accent]} />
      </svg>
    </Tooltip>
  );
}

interface KpiProps {
  label: string;
  value: string;
  sublabel?: string;
  accent: Accent;
  sparkValues?: number[];
  sizeVariant: SizeVariant;
  tooltip?: ReactNode;
}

function KpiCard({ label, value, sublabel, accent, sparkValues, sizeVariant, tooltip }: KpiProps) {
  const showSpark = sizeVariant !== "xs" && !!sparkValues && sparkValues.length > 0;
  const showSub = sizeVariant !== "xs" && !!sublabel;
  return (
    <div className={`kpi-widget kpi-widget--${accent} kpi-widget--${sizeVariant}`}>
      <div className="kpi-widget__top">
        <span className="kpi-widget__label">
          {label}
          {tooltip && (
            <Tooltip content={tooltip}>
              <span className="info-hint" aria-label="About this metric">
                i
              </span>
            </Tooltip>
          )}
        </span>
        {showSpark && (
          <span className="kpi-widget__spark-trend">Last {sparkValues!.length}d</span>
        )}
      </div>
      <span className="kpi-widget__value">{value}</span>
      {showSub && <span className="kpi-widget__sub">{sublabel}</span>}
      {showSpark && <Sparkline values={sparkValues!} accent={accent} />}
    </div>
  );
}

function KpiLoading({ sizeVariant }: { sizeVariant: SizeVariant }) {
  return (
    <div className={`kpi-widget kpi-widget--loading kpi-widget--${sizeVariant}`}>—</div>
  );
}

export function FlightCountWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  const sparks = useMemo(
    () => (data ? dailyBuckets(data.flight_dates ?? [], 7) : []),
    [data],
  );
  if (isLoading) return <KpiLoading sizeVariant={sizeVariant} />;
  const total = data?.totals.flight_count ?? 0;
  const ready = data?.totals.ready_flights ?? 0;
  const failed = data?.totals.failed_flights ?? 0;
  return (
    <KpiCard
      label="Flights"
      value={total.toString()}
      sublabel={`${ready} ready · ${failed} failed`}
      accent="orange"
      sparkValues={sparks}
      sizeVariant={sizeVariant}
      tooltip={
        <>
          <strong>Total flights</strong>
          <span>
            All replays you have visibility on. L size adds a 7-day sparkline
            showing daily flight counts.
          </span>
          <dl>
            <dt>Ready</dt>
            <dd>{ready}</dd>
            <dt>Failed</dt>
            <dd>{failed}</dd>
          </dl>
        </>
      }
    />
  );
}

export function TotalHoursWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  if (isLoading) return <KpiLoading sizeVariant={sizeVariant} />;
  const secs = data?.totals.total_duration_seconds ?? 0;
  const avg = data?.totals.average_duration_seconds ?? 0;
  const mins = Math.round(secs / 60);
  const display =
    sizeVariant === "xs"
      ? `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`
      : formatDuration(secs);
  return (
    <KpiCard
      label="Total time"
      value={display}
      sublabel={avg ? `avg ${formatDuration(avg)}` : undefined}
      accent="cyan"
      sizeVariant={sizeVariant}
      tooltip={
        <>
          <strong>Accumulated flight time</strong>
          <span>
            Sum of all ready-flight durations. Average is per-flight mean.
          </span>
        </>
      }
    />
  );
}

export function TotalDistanceWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  if (isLoading) return <KpiLoading sizeVariant={sizeVariant} />;
  const km = data?.totals.total_distance_km ?? 0;
  return (
    <KpiCard
      label="Distance"
      value={formatMetric(km, km >= 100 ? 0 : 1, " km")}
      sublabel={sizeVariant !== "xs" ? `~ ${Math.round(km / 10) * 10} km traveled` : undefined}
      accent="yellow"
      sizeVariant={sizeVariant}
      tooltip={
        <>
          <strong>Total distance flown</strong>
          <span>
            Sum of horizontal path length across all flights, computed from
            telemetry (not straight-line).
          </span>
        </>
      }
    />
  );
}

export function MaxAltitudeWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  if (isLoading) return <KpiLoading sizeVariant={sizeVariant} />;
  const alt = data?.totals.max_altitude_m ?? null;
  return (
    <KpiCard
      label="Max altitude"
      value={formatMetric(alt, 0, " m")}
      sublabel={sizeVariant !== "xs" ? "all-time peak" : undefined}
      accent="orange"
      sizeVariant={sizeVariant}
      tooltip={
        <>
          <strong>Altitude peak</strong>
          <span>
            Highest altitude (above sea level) recorded across any of your
            flights.
          </span>
        </>
      }
    />
  );
}

export function MissionCountWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  if (isLoading) return <KpiLoading sizeVariant={sizeVariant} />;
  const missions = data?.totals.mission_count ?? 0;
  const maps = data?.totals.map_count ?? 0;
  return (
    <KpiCard
      label="Missions"
      value={missions.toString()}
      sublabel={`${maps} map${maps === 1 ? "" : "s"}`}
      accent="green"
      sizeVariant={sizeVariant}
      tooltip={
        <>
          <strong>Unique missions</strong>
          <span>Distinct mission templates you have flown.</span>
        </>
      }
    />
  );
}

export function StreakWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  const sparks = useMemo(
    () => (data ? dailyBuckets(data.flight_dates ?? [], 14) : []),
    [data],
  );
  if (isLoading) return <KpiLoading sizeVariant={sizeVariant} />;
  const streak = data?.activity.streak_days ?? 0;
  const last7 = data?.activity.flights_last_7_days ?? 0;
  return (
    <KpiCard
      label="Streak"
      value={`${streak}d`}
      sublabel={`${last7} flight${last7 === 1 ? "" : "s"} this week`}
      accent="red"
      sparkValues={sparks}
      sizeVariant={sizeVariant}
      tooltip={
        <>
          <strong>Consecutive days flying</strong>
          <span>
            Streak resets after a day with zero flights. L size shows the
            last 14 days as a sparkline.
          </span>
        </>
      }
    />
  );
}

export function LongestFlightWidget({ sizeVariant }: WidgetComponentProps) {
  const { data, isLoading } = useSummaryQuery();
  if (isLoading) return <KpiLoading sizeVariant={sizeVariant} />;
  const longest = data?.totals.longest_flight;
  if (!longest) {
    return (
      <KpiCard label="Longest flight" value="—" accent="cyan" sizeVariant={sizeVariant} />
    );
  }
  return (
    <KpiCard
      label="Longest flight"
      value={formatDuration(longest.duration_seconds)}
      sublabel={sizeVariant !== "xs" ? longest.title ?? undefined : undefined}
      accent="cyan"
      sizeVariant={sizeVariant}
      tooltip={
        <>
          <strong>Personal best duration</strong>
          <span>{longest.title ?? "Your longest continuous flight."}</span>
        </>
      }
    />
  );
}
